/**
 * T13: Render-Helfer für die neuen 3g-Kartentypen — pur, ohne React/DOM.
 *
 * (a) Zulagenblatt (timesheets, data.manual_doc_type/meta.doc_type='zulagenblatt',
 *     Ergebnis-Shape aus ocr/zulagenblatt.py: days[{tag, zulagen:{...}, urlaubstag}]).
 * (b) Excel-Import (expenses, kind='excel_import', Shape aus
 *     hub/src/lib/xlsx-import.ts: rows/summen/nicht_exportierbar).
 *
 * Vertrags-Hinweis Detection: der Worker ERSETZT data beim Erfolgs-Merge
 * (worker/src/handlers/ocr.ts enrichedData) — data.manual_doc_type überlebt
 * dort NICHT, wohl aber meta.doc_type aus dem OCR-Ergebnis. Im fail-loud-Fall
 * (pending_review via markPendingReview, jsonb-Merge) ist es umgekehrt:
 * manual_doc_type bleibt, meta.doc_type fehlt. isZulagenblattData prüft BEIDE
 * (via docType→View-Registry, #466).
 */
import { timesheetViewFor } from "./docTypeView";

/** Ein Tag des Zulagenblatts (ocr/zulagenblatt.py, Zeilen-Normalizer). */
export interface ZulagenblattDay {
  tag: number;
  /** menge-/stunden-Spalten laut ocr/config/3g/zulagenblatt.json (0 = leer). */
  zulagen: Record<string, number>;
  /** marker-Spalte "Bewilligter Urlaubstag" — Tages-Flag, nicht Teil von zulagen. */
  urlaubstag?: boolean;
}

/** Unsicher gelesene Zelle (meta.zellen_unklar) — Wert steht auf 0, LAUT markiert. */
export interface ZulagenZelleUnklar {
  tag: number;
  spalte: string;
  raw?: unknown;
}

/** Kanonische Spalten-Reihenfolge + deutsche Labels. Deckt beide Config-
 *  Varianten ab (alt: "schicht", aktuell: "schicht_30" — 12-Spalten-Layout
 *  nach Echtdaten-Sichtung 09.07.). Unbekannte Keys aus den Daten werden
 *  hinten angehängt (Key als Label) statt still verschluckt. */
export const ZULAGEN_SPALTEN: ReadonlyArray<{ key: string; label: string }> = [
  { key: "untertage", label: "Untertage" },
  { key: "baustelle", label: "Baustelle" },
  { key: "hoehe", label: "Höhe" },
  { key: "trennung", label: "Trennung" },
  { key: "naechtigung", label: "Nächtigung" },
  { key: "schicht_30", label: "Schicht (30%)" },
  { key: "schicht", label: "Schicht" },
  { key: "ueberstunden_25", label: "Üst. 25%" },
  { key: "ueberstunden_50", label: "Üst. 50%" },
  { key: "ueberstunden_100", label: "Üst. 100%" },
  // EFZG (Thomas 13.08.): Seit die Spalte auf Lohnart 152 exportiert wird, MUSS
  // sie hier stehen. Vorher war ein Lesefehler folgenlos — der Wert ging
  // nirgendwohin. Jetzt wird daraus eine Lohnzeile, und ohne diese Zeile saehe
  // Franka den Wert NUR unten im Berechnungsergebnis: sichtbar, aber nicht mehr
  // korrigierbar. Eine Zahl in die Abrechnung zu lassen, die der Mensch nicht
  // gegen den Scan halten und ueberschreiben kann, ist genau der Fehler, den wir
  // heute frueh beim ZA-Konto behoben haben.
  { key: "urlaubstag", label: "Bewilligter Urlaubstag" },
];

/** Ist diese Timesheet-Karte ein 3g-Zulagenblatt? Delegiert an die
 *  docType→View-Registry (#466, Single-Source) — true genau dann, wenn die
 *  Registry für den doc_type der Karte die Zulagenblatt-View wählt. */
export function isZulagenblattData(data: unknown): boolean {
  return timesheetViewFor(data) === "zulagenblatt";
}

/** Fail-loud-Fehler der Karte (state=pending_review): erster review_attempts-
 *  Eintrag mit body.error — z. B. grid_mismatch mit detail.erwartet/gefunden.
 *  null wenn kein Fehler vorliegt (normale Karte). */
export function zulagenblattReviewError(
  data: unknown,
): { error: string; detail: Record<string, unknown> } | null {
  const attempts = (data as { review_attempts?: unknown } | null | undefined)?.review_attempts;
  if (!Array.isArray(attempts)) return null;
  for (const a of attempts) {
    const body = (a as { body?: { error?: unknown; detail?: unknown } } | null)?.body;
    if (body && typeof body.error === "string" && body.error.length > 0) {
      const detail = body.detail && typeof body.detail === "object"
        ? (body.detail as Record<string, unknown>)
        : {};
      return { error: body.error, detail };
    }
  }
  return null;
}

/**
 * ABGELEITETE Felder — stehen auf keinem Blatt, der Mensch traegt sie ein.
 *
 * Karte 720 (Franka 13.08.2026, Feiertage): Der Feiertag laesst sich bei 3G nicht
 * ableiten — vom Zulagenblatt wird nur die aufgedruckte Monats-Summenzeile
 * gelesen, nie die Tageszeilen. Welchen Tag jemand gearbeitet hat, steht dort
 * nicht. Deshalb kein Automatismus, sondern ein Eingabefeld.
 *
 * WARUM JEDER EINTRAG EINEN `pdf`-NAMEN TRAEGT — die wichtigste Zeile dieser Datei:
 * Das Feld erscheint nur, wenn das REGELWERK diesen pdf-Namen kennt. Nicht, weil
 * es hier steht.
 *
 * Gemessen am 13.08.2026 auf prod: Nach dem Entfernen der Matrix-Zeile blieb das
 * Feld stehen — und die Berechnung ignoriert einen dort eingetragenen Wert
 * STILLSCHWEIGEND (`berechneZulagenblattSummen` speist die Spalte nur ein, wenn
 * die Matrix den pdf-Namen kennt). Franka haette eine Zahl getippt, sie gesehen,
 * und im Export waere sie nicht erschienen. Ein Eingabefeld, das den Wert
 * verschluckt, ist schlimmer als keines.
 *
 * Dasselbe Muster — eine Wahrheit an zwei Orten — ist an diesem Tag VIERMAL
 * aufgetreten (Unique-Index, Konfliktpruefung, Rechen-Gate, Feld/Matrix). Hier ist
 * es strukturell ausgeschlossen: eine Liste, und sie fragt das Regelwerk.
 */
export const ABGELEITETE_SPALTEN: ReadonlyArray<{ key: string; label: string; pdf: string }> = [
  { key: "feiertag", label: "Feiertage (EFZG)", pdf: "Feiertag [EFZG]" },
  // Karte 726 (Franka 14.08.): Krankenstandstage zaehlen ebenfalls auf die
  // EFZG-Lohnart. Das Kuerzel "K" steht bei den einzelnen Tagen — gelesen wird
  // nur die Summenzeile. Also dieselbe Form: ein Feld, das der Mensch fuellt.
  // KEIN Kalender-Hinweis wie beim Feiertag: einen Krankenstand weiss nur das Blatt.
  { key: "krankenstand", label: "Krankenstandstage (EFZG)", pdf: "Krankenstand [EFZG]" },
];

/** Normalisierung wie im Worker (`normalisierePdfName`): Vergleich ohne
 *  Gross-/Kleinschreibung und ohne Randleerzeichen. */
function pdfKey(s: string): string {
  return s.trim().toLowerCase();
}

/**
 * Die abgeleiteten Felder, die das Regelwerk dieses Mandanten wirklich kennt.
 * `matrixPdfNamen` = die `pdf`-Werte aus `uebersetzungsmatrix.zulagen`.
 * Ohne Matrix (noch nicht geladen, fail-soft) → leer: lieber kein Feld als eines
 * ohne Wirkung.
 */
export function abgeleiteteSpaltenFuer(
  matrixPdfNamen: ReadonlyArray<string> | null | undefined,
): Array<{ key: string; label: string }> {
  if (!matrixPdfNamen || matrixPdfNamen.length === 0) return [];
  const bekannt = new Set(matrixPdfNamen.map(pdfKey));
  return ABGELEITETE_SPALTEN.filter((s) => bekannt.has(pdfKey(s.pdf)))
    .map((s) => ({ key: s.key, label: s.label }));
}

export function zulagenKatalog(
  data: unknown,
  matrixPdfNamen?: ReadonlyArray<string> | null,
): Array<{ key: string; label: string }> {
  const abgeleitet = abgeleiteteSpaltenFuer(matrixPdfNamen);
  const roh = (data as { spalten_katalog?: unknown } | null | undefined)?.spalten_katalog;
  if (Array.isArray(roh)) {
    const aus = roh
      .filter((s): s is { key: string; label?: unknown } =>
        !!s && typeof s === "object" && typeof (s as { key?: unknown }).key === "string")
      .map((s) => {
        const bekannt = ZULAGEN_SPALTEN.find((z) => z.key === s.key);
        return {
          key: s.key,
          // Label aus der kanonischen Liste gewinnt: sie trägt die deutschen
          // Bezeichnungen, die Franka kennt. Die Config liefert nur den Fallback.
          label: bekannt?.label ?? (typeof s.label === "string" ? s.label : s.key),
        };
      });
    if (aus.length > 0) return [...aus, ...abgeleitet];
  }
  return [
    ...ZULAGEN_SPALTEN.filter((s) => s.key !== "schicht").map((s) => ({ ...s })),
    ...abgeleitet,
  ];
}

/** Spalten für die Tages-Tabelle: der Feldkatalog der Karte, IMMER vollständig —
 *  plus unbekannte Keys aus den Daten hinten angehängt (Key als Label), damit
 *  auch Gelesenes nie verschluckt wird. Vorher wurde auf `present` gefiltert;
 *  das machte die Lesung zur Autorität über die Feldliste (Thomas 13.08.). */
export function zulagenColumns(
  days: ReadonlyArray<ZulagenblattDay>,
  data?: unknown,
  matrixPdfNamen?: ReadonlyArray<string> | null,
): Array<{ key: string; label: string }> {
  const katalog = zulagenKatalog(data, matrixPdfNamen);
  const katalogKeys = new Set(katalog.map((s) => s.key));
  const present = new Set<string>();
  for (const d of days) {
    for (const k of Object.keys(d.zulagen ?? {})) present.add(k);
  }
  const unknown = [...present]
    .filter((k) => !katalogKeys.has(k))
    .sort()
    .map((k) => ({ key: k, label: ZULAGEN_SPALTEN.find((z) => z.key === k)?.label ?? k }));
  return [...katalog, ...unknown];
}

/** Summen je Spalte aus days berechnet (Vergleichswert zur aufgedruckten
 *  Blatt-Summe) + Anzahl der Urlaubstage. Nicht-numerische Werte zählen 0. */
export function zulagenSummen(
  days: ReadonlyArray<ZulagenblattDay>,
  columns: ReadonlyArray<{ key: string }>,
): { spalten: Record<string, number>; urlaubstage: number } {
  const spalten: Record<string, number> = {};
  for (const c of columns) spalten[c.key] = 0;
  let urlaubstage = 0;
  for (const d of days) {
    for (const c of columns) {
      const v = d.zulagen?.[c.key];
      if (typeof v === "number" && Number.isFinite(v)) spalten[c.key]! += v;
    }
    if (d.urlaubstag === true) urlaubstage += 1;
  }
  // Float-Artefakte aus Dezimal-Additionen glätten (2.5+0.5+… → 3, nicht 2.9999…).
  for (const k of Object.keys(spalten)) spalten[k] = Math.round(spalten[k]! * 100) / 100;
  return { spalten, urlaubstage };
}

/** Lookup-Set der unsicher gelesenen Zellen: "tag:spalte". */
export function zellenUnklarSet(zellenUnklar: unknown): ReadonlySet<string> {
  const out = new Set<string>();
  if (!Array.isArray(zellenUnklar)) return out;
  for (const z of zellenUnklar) {
    const tag = (z as ZulagenZelleUnklar | null)?.tag;
    const spalte = (z as ZulagenZelleUnklar | null)?.spalte;
    if (typeof tag === "number" && typeof spalte === "string") out.add(`${tag}:${spalte}`);
  }
  return out;
}

/** Mengen-/Stunden-Wert fürs UI: 0 → "" (dezent leer), sonst deutsches
 *  Dezimalkomma ohne überflüssige Nullen (2.5 → "2,5", 8 → "8"). */
export function formatMenge(v: number | undefined | null): string {
  if (typeof v !== "number" || !Number.isFinite(v) || v === 0) return "";
  return String(Math.round(v * 100) / 100).replace(".", ",");
}

/** €-Format de-AT: 1234.5 → "1.234,50 €". Für die Excel-Import-Kosten. */
export function formatEuro(v: number | undefined | null): string {
  const n = typeof v === "number" && Number.isFinite(v) ? v : 0;
  return `${n.toLocaleString("de-AT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

// ─── card #522: editierbare Blatt-Summenzeile ──────────────────────────────
// Zulagenblatt ist summe-basiert (data.blatt_summe.summen, flaches 9-Key-Objekt),
// NICHT tages-basiert (T23: nur die aufgedruckte Monatssumme ist der Lohnwert).
// Die Edit-Logik lebt hier als REINE Funktionen (Muster von zulagenblattHighlight-
// Model 2026-07-14: testbar, statt inline in der Komponente = nicht testbar).

/** Read-only-States des Zulagenblatts — Spiegel des Server-Gates
 *  (hub/src/routes/timesheets.ts PATCH /:id) und der Stundenlisten-View
 *  (TimesheetDetailPage READONLY_STATES). Ab diesen States lehnt der Server jeden
 *  PATCH mit 409 card_read_only ab → die Summen-Inputs MÜSSEN disabled sein, sonst
 *  editiert der User scheinbar bis zum Speicher-Fehler (Glass-Box-Bruch). */
export const ZULAGENBLATT_READONLY_STATES: ReadonlySet<string> = new Set([
  "calculated", "exported", "archived", "superseded",
]);

/** true = die Karte ist in einem read-only-State (Summen nicht editierbar). */
export function isZulagenblattReadOnly(state: string | null | undefined): boolean {
  return ZULAGENBLATT_READONLY_STATES.has(state ?? "");
}

/** Inverse von formatMenge: deutsches Dezimalkomma-Eingabefeld → Zahl.
 *  "" → 0 (leeres Feld = Null-Summe), "2,5" → 2.5, "8" → 8, "2.5" → 2.5.
 *  Nicht-numerische Eingabe → null (Aufrufer blockt Speichern + meldet LAUT,
 *  statt einen NaN still ins Lohn-Datum zu schreiben). */
export function parseMengeInput(input: string): number | null {
  const s = input.trim().replace(/\s/g, "");
  if (s === "") return 0;
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/** Editier-State der Summenzeile aus der gelesenen Blatt-Summe seeden — genau die
 *  Keys, die die read-only-Tabelle zeigt (ZULAGEN_SPALTEN ∩ numerische Summe).
 *  0 → "" (dezent leer, round-trippt via parseMengeInput zurück auf 0). */
export function seedSummenEdits(
  blattSumme: Record<string, unknown> | null | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!blattSumme) return out;
  for (const c of [...ZULAGEN_SPALTEN, ...ABGELEITETE_SPALTEN]) {
    const v = blattSumme[c.key];
    if (typeof v === "number") out[c.key] = formatMenge(v);
  }
  return out;
}

/** Editier-Strings → numerisches Summen-Objekt fürs PATCH-Payload. `invalid`
 *  sammelt Keys mit nicht-parsebarer Eingabe: Speichern wird geblockt (nicht
 *  still verworfen), der Nutzer bekommt LAUT gesagt, welche Zelle klemmt. */
export function buildSummenPatch(
  summenEdits: Record<string, string>,
): { summen: Record<string, number>; invalid: string[] } {
  const summen: Record<string, number> = {};
  const invalid: string[] = [];
  for (const [key, raw] of Object.entries(summenEdits)) {
    const n = parseMengeInput(raw);
    if (n === null) { invalid.push(key); continue; }
    summen[key] = n;
  }
  return { summen, invalid };
}

export type ZulagenblattHighlightModel = {
  /** true = spaltengenaues Scan-Overlay möglich (Geometrie + Band + Summenzeile da). */
  highlightOn: boolean;
  cells: Array<{ key: string; leftPct: number; rightPct: number }>;
  bandTopPct: number;
  bandBottomPct: number;
  /** Warum KEIN Highlight — für den NICHT-STILLEN Hinweis in der UI. null = alles ok.
   *  'kein_spalten_geom' = Karte vor dem Highlight-Feature (T21, spalten_geom in
   *  meta.layout, Commit f043f12 2026-07-13) gelesen → neu einlesen erzeugt es. */
  reason: null | "kein_spalten_geom" | "kein_band" | "keine_summenzeile";
};

/**
 * BUG-Fix 2026-07-14: die Highlight-Eignung des Zulagenblatts als REINE, getestete
 * Funktion (vorher inline in ZulagenblattDetail — nicht testbar, still degradierend).
 * Die per-Spalte-x-Geometrie ist genau die, die die OCR zum Lesen nutzte
 * (data.meta.layout.spalten_geom[<key>]); das y-Band der Summenzeile ist
 * data.blatt_summe.band_pct; gehighlightet werden nur Spalten mit gelesener Summe.
 * Der `reason` macht das Fehlen SICHTBAR (statt stillem Bild-Fallback).
 */
export function zulagenblattHighlightModel(data: unknown): ZulagenblattHighlightModel {
  const d = (data ?? {}) as {
    meta?: { layout?: { spalten_geom?: Record<string, { leftPct?: unknown; rightPct?: unknown }> } };
    blatt_summe?: { band_pct?: unknown; summen?: Record<string, unknown> };
  };
  const spaltenGeom = d.meta?.layout?.spalten_geom;
  const blattSumme = d.blatt_summe?.summen;
  const bandRaw = d.blatt_summe?.band_pct;
  const bandOk = Array.isArray(bandRaw) && bandRaw.length === 2 &&
    typeof bandRaw[0] === "number" && typeof bandRaw[1] === "number";
  const bandTopPct = bandOk ? Math.min(bandRaw[0] as number, bandRaw[1] as number) * 100 : 0;
  const bandBottomPct = bandOk ? Math.max(bandRaw[0] as number, bandRaw[1] as number) * 100 : 0;
  const cells = (bandOk && spaltenGeom && blattSumme)
    ? ZULAGEN_SPALTEN
        .filter((c) => typeof blattSumme[c.key] === "number")
        .map((c) => {
          const g = spaltenGeom[c.key];
          if (!g || typeof g.leftPct !== "number" || typeof g.rightPct !== "number") return null;
          return { key: c.key, leftPct: (g.leftPct as number) * 100, rightPct: (g.rightPct as number) * 100 };
        })
        .filter((x): x is { key: string; leftPct: number; rightPct: number } => x !== null)
    : [];
  const highlightOn = cells.length > 0;
  let reason: ZulagenblattHighlightModel["reason"] = null;
  if (!highlightOn) {
    if (!blattSumme || Object.keys(blattSumme).length === 0) reason = "keine_summenzeile";
    else if (!bandOk) reason = "kein_band";
    else reason = "kein_spalten_geom";
  }
  return { highlightOn, cells, bandTopPct, bandBottomPct, reason };
}
